declare const nonEmptyStringBrand: unique symbol;
declare const uniqueBrand: unique symbol;

/** A string whose non-empty length has been checked. */
export type NonEmptyString = string & {
    readonly [nonEmptyStringBrand]: true;
};

/**
 * Checks for a non-empty string without trimming or normalizing its contents.
 *
 * @example
 * isNonEmptyString('token'); // true
 */
export const isNonEmptyString = (value: unknown): value is NonEmptyString =>
    typeof value === 'string' && value.length > 0;

/** A read-only array containing at least one element. */
export type NonEmptyArray<T> = readonly [T, ...T[]];

/**
 * Checks that an array contains at least one element.
 *
 * @example
 * isNonEmptyArray(['token']); // true
 */
export const isNonEmptyArray = <T>(
    values: readonly T[],
): values is NonEmptyArray<T> => values.length > 0;

/** An array checked for uniqueness using Set equality, including object identity. */
export type UniqueArray<T> = readonly T[] & {readonly [uniqueBrand]: true};

/**
 * Checks uniqueness using Set equality; an empty array is unique.
 *
 * @example
 * isUniqueArray(['token', 'token']); // false
 */
export const isUniqueArray = <T>(
    values: readonly T[],
): values is UniqueArray<T> => new Set(values).size === values.length;

/** A read-only array checked to contain at least one element and no duplicates. */
export type UniqueNonEmptyArray<T> = UniqueArray<T> & NonEmptyArray<T>;

/**
 * Checks that an array is both non-empty and unique.
 *
 * @example
 * isUniqueNonEmptyArray(['token']); // true
 */
export const isUniqueNonEmptyArray = <T>(
    values: readonly T[],
): values is UniqueNonEmptyArray<T> =>
    isNonEmptyArray(values) && isUniqueArray(values);

/**
 * Checks non-empty string elements, a non-empty array, and case-sensitive uniqueness.
 *
 * @example
 * isUniqueNonEmptyArrayOfNonEmptyString(['token', 'Token']); // true
 */
export const isUniqueNonEmptyArrayOfNonEmptyString = (
    values: readonly unknown[],
): values is UniqueNonEmptyArray<NonEmptyString> => {
    if (isUniqueNonEmptyArray(values)) {
        for (const value of values) {
            if (!isNonEmptyString(value)) {
                return false;
            }
        }
        return true;
    }
    return false;
};
